import { computed, onUnmounted, shallowRef } from 'vue'
import type { AppLaunchPlatform, AppLaunchTarget, AppPlatformLaunchConfig } from '@/constants/app'
import { APP_LAUNCH_CONFIG, APP_PAGE_TIMING } from '@/constants/app'

export type AppPageMaskKind = 'wechat' | 'qr'

interface UseAppLaunchOptions {
  onShowOverlay?: () => void
  onHideOverlay?: () => void
}

function getUserAgent() {
  return typeof navigator === 'undefined' ? '' : navigator.userAgent.toLowerCase()
}

function detectPlatform(): AppLaunchPlatform {
  const ua = getUserAgent()

  if (/iphone|ipad|ipod/.test(ua)) {
    return 'ios'
  }

  if (/android/.test(ua)) {
    return 'android'
  }

  return 'other'
}

function isWeChatMobile() {
  const ua = getUserAgent()
  const isWeChat = /micromessenger/i.test(ua)
  const isMobile = /android|iphone|ipad|ipod|mobile/i.test(ua)

  return isWeChat && isMobile
}

function isMobileBrowser() {
  return /android|iphone|ipad|ipod|mobile/i.test(getUserAgent())
}

function buildLaunchTarget(
  platform: AppLaunchPlatform,
  target: AppPlatformLaunchConfig,
): AppLaunchTarget | null {
  if (platform === 'ios') {
    if (target.universalLink) {
      return { type: 'web', url: target.universalLink }
    }

    if (target.scheme) {
      return { type: 'custom', url: target.scheme }
    }

    return null
  }

  if (platform === 'android') {
    if (target.appLink) {
      return { type: 'web', url: target.appLink }
    }

    if (target.intent) {
      return { type: 'custom', url: target.intent }
    }

    if (target.scheme) {
      return { type: 'custom', url: target.scheme }
    }
  }

  return null
}

function openCustomScheme(url: string) {
  const iframe = document.createElement('iframe')
  iframe.style.display = 'none'
  iframe.src = url
  document.body.appendChild(iframe)

  window.setTimeout(() => {
    iframe.remove()
  }, 1000)
}

function openLaunchTarget(launch: AppLaunchTarget) {
  if (launch.type === 'custom') {
    openCustomScheme(launch.url)
    return
  }

  window.location.href = launch.url
}

export function useAppLaunch(options: UseAppLaunchOptions = {}) {
  const activeMask = shallowRef<AppPageMaskKind | null>(null)
  const toastMessage = shallowRef('')
  const isOverlayOpen = computed(() => activeMask.value !== null)

  let toastTimer: ReturnType<typeof window.setTimeout> | null = null

  function clearToastTimer() {
    if (toastTimer === null) {
      return
    }

    window.clearTimeout(toastTimer)
    toastTimer = null
  }

  function showToast(message: string) {
    clearToastTimer()
    toastMessage.value = message

    toastTimer = window.setTimeout(() => {
      toastMessage.value = ''
      toastTimer = null
    }, APP_PAGE_TIMING.toastDurationMs)
  }

  function showMask(kind: AppPageMaskKind) {
    options.onShowOverlay?.()
    activeMask.value = kind
  }

  function hideMask() {
    if (!activeMask.value) {
      return
    }

    activeMask.value = null
    options.onHideOverlay?.()
  }

  function openAppWithFallback(launch: AppLaunchTarget, fallbackUrl: string) {
    let pageHidden = false
    const startTime = Date.now()

    const handleVisibilityChange = () => {
      if (document.hidden) {
        pageHidden = true
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    openLaunchTarget(launch)

    window.setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)

      if (pageHidden || Date.now() - startTime < APP_PAGE_TIMING.minValidDelayMs) {
        return
      }

      if (fallbackUrl) {
        window.location.href = fallbackUrl
        return
      }

      showToast('未安装App，下载地址待配置')
    }, APP_PAGE_TIMING.fallbackDelayMs)
  }

  function tryOpenApp() {
    if (isWeChatMobile()) {
      showMask('wechat')
      return
    }

    if (!isMobileBrowser()) {
      showMask('qr')
      return
    }

    const platform = detectPlatform()

    if (platform === 'other') {
      showMask('qr')
      return
    }

    const target = APP_LAUNCH_CONFIG[platform]
    const launch = buildLaunchTarget(platform, target)

    if (!launch) {
      showToast('拉起规则待配置')
      return
    }

    openAppWithFallback(launch, target.downloadUrl || APP_LAUNCH_CONFIG.defaultDownloadUrl)
  }

  onUnmounted(() => {
    clearToastTimer()
  })

  return {
    activeMask,
    isOverlayOpen,
    toastMessage,
    hideMask,
    tryOpenApp,
  }
}
